import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// Types for metadata
export interface SessionMetadata {
  id: string;
  startedAt: string;
  userId?: string;
  tenantId?: string;
}

export interface WorkflowMetadata {
  contextId: string;
  workflowId?: string;
  scriptId?: string;
  phase: number;
  status: 'active' | 'interrupted' | 'completed' | 'error';
}

export interface TaskIdentificationMetadata {
  domain: string | null;
  task: string | null;
  method: 'ai' | 'regex' | 'manual';
  identifiedAt: string;
  responseTimeMs: number;
  apiCalls?: number;
  cacheHit?: boolean;
}

export interface KnowledgeBaseMetadata {
  framework?: string;
  domainRules?: string;
  safetyChecks: boolean;
  customizations?: string[];
}

export interface PerformanceMetadata {
  totalLatencyMs: number;
  breakdownMs?: Record<string, number>;
}

export interface AuditMetadata {
  createdBy?: string;
  createdAt?: string;
  lastModified?: string;
  modifiedBy?: string;
  version?: number;
}

export interface WorkflowMetadataState {
  session?: SessionMetadata;
  workflow?: WorkflowMetadata;
  taskIdentification?: TaskIdentificationMetadata;
  knowledgeBase?: KnowledgeBaseMetadata;
  performance?: PerformanceMetadata;
  audit?: AuditMetadata;
}

export interface InterruptState {
  isInterrupting: boolean;
  canResume: boolean;
  savedContext?: any;
  interruptedAt?: string;
  reason?: string;
}

interface WorkflowMetadataContextType {
  metadata: WorkflowMetadataState;
  interrupt: InterruptState;
  updateMetadata: (category: keyof WorkflowMetadataState, data: any) => void;
  clearMetadata: () => void;
  setInterruptState: (state: Partial<InterruptState>) => void;
  clearInterruptState: () => void;
}

const WorkflowMetadataContext = createContext<WorkflowMetadataContextType | undefined>(undefined);

export const WorkflowMetadataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [metadata, setMetadata] = useState<WorkflowMetadataState>({});
  const [interrupt, setInterrupt] = useState<InterruptState>({
    isInterrupting: false,
    canResume: false
  });

  const updateMetadata = useCallback((category: keyof WorkflowMetadataState, data: any) => {
    setMetadata(prev => ({
      ...prev,
      [category]: data
    }));
  }, []);

  const clearMetadata = useCallback(() => {
    setMetadata({});
  }, []);

  const setInterruptState = useCallback((state: Partial<InterruptState>) => {
    setInterrupt(prev => ({
      ...prev,
      ...state
    }));
  }, []);

  const clearInterruptState = useCallback(() => {
    setInterrupt({
      isInterrupting: false,
      canResume: false
    });
  }, []);

  const value = {
    metadata,
    interrupt,
    updateMetadata,
    clearMetadata,
    setInterruptState,
    clearInterruptState
  };

  return (
    <WorkflowMetadataContext.Provider value={value}>
      {children}
    </WorkflowMetadataContext.Provider>
  );
};

export const useWorkflowMetadata = () => {
  const context = useContext(WorkflowMetadataContext);
  if (!context) {
    throw new Error('useWorkflowMetadata must be used within WorkflowMetadataProvider');
  }
  return context;
};